/**
 * OnboardingFlow
 *
 * Top-level shell for the multi-step merchant application wizard.
 *
 * Layout is a locked-height flex column (`h-[100dvh]`, `overflow-hidden`)
 * split into three zones:
 *
 *   ┌────────────┬────────────────────────────────────────┐
 *   │            │  Header (step title + description)    │
 *   │  StepRail  ├────────────────────────────────────────┤
 *   │  sidebar   │                                        │
 *   │  (lg+)     │  Main content — the ONLY scroll area  │
 *   │            │                                        │
 *   │            ├────────────────────────────────────────┤
 *   │            │  Sticky footer (error + nav buttons)   │
 *   └────────────┴────────────────────────────────────────┘
 *
 * Only the middle "Main content" zone scrolls — the header and footer
 * are pinned to the viewport, so the Next/Back buttons are ALWAYS
 * visible regardless of step length or viewport height.
 *
 * Below the `lg` breakpoint the sidebar collapses and a compact
 * `MobileStepIndicator` takes over at the top.
 *
 * This component owns no form state — each step is an isolated form
 * that auto-saves to the API via `useSaveDraft`. Navigation is driven
 * by `nextStep` / `previousStep` / `goToStep` mutations.
 *
 * Loading state: when `application` is undefined the flow renders the
 * real chrome (StepRail, header, nav footer) from the static STEP_CONFIG
 * and replaces the step body with a `StepBodySkeleton`. Mirrors the
 * pattern used by AdminLoadingFallback / ManagementLoadingFallback.
 */

import type { IOnboardingApplication } from "@order-up/models";
import { PrivacyLinks } from "@order-up/common";
import { Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  useGoToStep,
  useNextStep,
  usePreviousStep,
  useSubmitApplication,
} from "../lib/hooks";
import {
  STEP_CONFIG,
  STEP_ORDER,
  type OnboardingApplicationData,
} from "../types/onboarding";
import { StepBodySkeleton } from "./StepBodySkeleton";
import {
  BusinessInfoStep,
  BusinessTypeStep,
  OwnersStep,
  PersonalInfoStep,
  LocationDetailsStep,
  ReviewStep,
} from "./steps";
import { MobileStepIndicator } from "./ui/MobileStepIndicator";
import { SignOutButton } from "./ui/SignOutButton";
import { StepNavigation } from "./ui/StepNavigation";
import { StepRail, type StepRailItem } from "./ui/StepRail";

export interface OnboardingFlowProps {
  /** The application being edited. Undefined while the initial fetch is in flight. */
  application?: IOnboardingApplication;
}

const TOTAL_STEPS = STEP_ORDER.length;

// Pre-compute the step rail items once — order matches STEP_ORDER.
const STEP_RAIL_ITEMS: StepRailItem[] = STEP_ORDER.map((key, index) => ({
  index,
  label: STEP_CONFIG[key].label,
  description: STEP_CONFIG[key].description,
}));

const NOOP = () => {};

export function OnboardingFlow({ application }: OnboardingFlowProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { mutate: goToStep } = useGoToStep();
  const { mutate: nextStep, isPending: isNextPending } = useNextStep();
  const { mutate: previousStep, isPending: isPrevPending } = usePreviousStep();
  const { mutate: submitApplication, isPending: isSubmitPending } =
    useSubmitApplication();

  const isLoading = !application;

  // Clamp to valid range — existing drafts may have a higher step from before
  // bank-account and expected-activity were removed. Default to step 0 while
  // the initial fetch is in flight.
  const currentStepIndex = application
    ? Math.min(application.currentStep, TOTAL_STEPS - 1)
    : 0;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === TOTAL_STEPS - 1;

  const currentStepKey = STEP_ORDER[currentStepIndex];
  const currentStepConfig = STEP_CONFIG[currentStepKey];

  const isBusy = isNextPending || isPrevPending || isSubmitPending;

  // Reset the scroll position to the top of the step body whenever the
  // user advances/retreats/jumps to a different step. Without this, a
  // long step (Personal Info, with the address section) leaves the next
  // step scrolled mid-form, which feels broken — the operator has to
  // hand-scroll back to the first field every time.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [currentStepIndex]);

  // ─── Navigation handlers ────────────────────────────────────────────
  const handleNext = () => {
    if (!application) return;
    setSubmitError(null);
    if (isLastStep) {
      handleSubmit();
    } else {
      nextStep(application.id);
    }
  };

  const handleBack = () => {
    if (!application || isFirstStep) return;
    setSubmitError(null);
    previousStep(application.id);
  };

  const handleJumpToStep = (stepIndex: number) => {
    if (!application) return;
    setSubmitError(null);
    goToStep({ id: application.id, step: stepIndex });
  };

  const handleSubmit = () => {
    if (!application) return;
    setSubmitError(null);
    submitApplication(application.id, {
      onError: (error: Error) => {
        setSubmitError(
          error.message || "Failed to submit application. Please try again.",
        );
      },
    });
  };

  // ─── Per-step completeness check ────────────────────────────────────
  const isStepValid = (): boolean => {
    if (!application) return false;

    const data = application.applicationData as OnboardingApplicationData;

    switch (currentStepIndex) {
      case 0: // Business Type
        return !!data.ownership?.ownershipTypeCd;
      case 1: {
        // Personal Info
        if (
          !data.ownership?.owner?.ownerName ||
          !data.ownership?.owner?.ownerEmail ||
          !data.ownership?.owner?.ownerDob
        )
          return false;
        // DOB must be 18+
        const dob = new Date(data.ownership.owner.ownerDob);
        if (isNaN(dob.getTime())) return false;
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const md = today.getMonth() - dob.getMonth();
        if (md < 0 || (md === 0 && today.getDate() < dob.getDate())) age--;
        return age >= 18;
      }
      case 2: // Business Info
        return !!(data.legalBusinessName && data.demographic?.businessPhone);
      case 3: {
        // Restaurant Details. Operating hours are required only when
        // `hoursMode === 'fixed'` — by-appointment merchants don't have a
        // weekly schedule, but the UI still seeds DEFAULT_OPERATING_HOURS
        // underneath so a switch back to fixed is data-preserving.
        const r = data.restaurant;
        if (!r?.concept || !r?.contactEmail) return false;
        const byAppointment = r.hoursMode === 'by_appointment';
        if (byAppointment) return true;
        return !!(
          r.operatingHours && Object.keys(r.operatingHours).length > 0
        );
      }
      case 4: // Owners (optional)
        return true;
      case 5: // Review
        return true;
      default:
        return false;
    }
  };

  const canProceed = isStepValid();

  // ─── Step renderer ──────────────────────────────────────────────────
  const renderStep = () => {
    if (!application) {
      return <StepBodySkeleton stepIndex={currentStepIndex} />;
    }

    switch (currentStepIndex) {
      case 0:
        return <BusinessTypeStep application={application} />;
      case 1:
        return <PersonalInfoStep application={application} />;
      case 2:
        return <BusinessInfoStep application={application} />;
      case 3:
        return <LocationDetailsStep application={application} />;
      case 4:
        return <OwnersStep application={application} />;
      case 5:
        return (
          <ReviewStep application={application} onEdit={handleJumpToStep} />
        );
      default:
        return <div className="text-muted">Invalid step</div>;
    }
  };

  return (
    // Locked to the viewport so the footer is always pinned.
    // `100dvh` handles the mobile address bar / iOS home indicator correctly.
    <div className="flex h-dvh w-full overflow-hidden bg-background text-foreground">
      {/* ─── Desktop sidebar (lg+) ──────────────────────────────────── */}
      <aside
        aria-label="Application progress"
        className="hidden lg:flex w-[320px] shrink-0 flex-col border-r border-white/5 bg-linear-to-b from-zinc-950 via-background to-background"
      >
        {/* Brand header */}
        <div className="flex items-center gap-3 px-6 pt-8 pb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-linear-to-br from-primary to-blue-400 shadow-[0_0_30px_rgba(59,130,246,0.35)]">
            <Sparkles className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
              OrderUp
            </span>
            <span className="truncate text-base font-semibold text-white">
              Onboarding Process
            </span>
          </div>
        </div>

        {/* Step rail — this is the only scroll area in the sidebar */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <StepRail
            items={STEP_RAIL_ITEMS}
            currentIndex={currentStepIndex}
            onNavigate={handleJumpToStep}
            disabled={isBusy || isLoading}
          />
        </div>

        {/* Sidebar footer — support hint + sign out */}
        <div className="border-t border-white/5 px-6 py-5 space-y-4">
          <p className="text-[11px] leading-relaxed text-muted">
            Need help filling this out? Email{" "}
            <a
              href="mailto:support@letsorderup.com"
              className="text-zinc-300 underline-offset-2 hover:text-white hover:underline"
            >
              support@letsorderup.com
            </a>
            .
          </p>
          <SignOutButton />
        </div>
      </aside>

      {/* ─── Main column ─────────────────────────────────────────────── */}
      <main className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top indicator (< lg) */}
        <div className="lg:hidden relative">
          <MobileStepIndicator
            current={currentStepIndex + 1}
            total={TOTAL_STEPS}
            currentLabel={currentStepConfig?.label}
          />
          <div className="absolute right-3 top-3">
            <SignOutButton
              variant="inline"
              className="py-1.5! px-2.5! text-xs!"
            />
          </div>
        </div>

        {/* Step header (desktop) — pinned above the scroll region */}
        <header className="hidden lg:block shrink-0 border-b border-white/5 px-12 pt-10 pb-8">
          <div className="mx-auto max-w-2xl">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
              <span>Step {currentStepIndex + 1}</span>
              <span className="h-px w-6 bg-border" />
              <span>of {TOTAL_STEPS}</span>
            </div>
            <h1 className="mt-3 text-3xl font-semibold leading-tight text-white">
              {currentStepConfig?.label}
            </h1>
            {currentStepConfig?.description && (
              <p className="mt-2 text-base text-muted">
                {currentStepConfig.description}
              </p>
            )}
          </div>
        </header>

        {/* Scrollable content — the ONLY scroll area in the main column.
            Ref attached so the step-change effect above can reset
            scrollTop=0 on every navigation. */}
        <div
          ref={scrollContainerRef}
          // Keyboard users must be able to scroll it (WCAG 2.1.1): a scroll
          // region with no focusable content yet — a step body still loading —
          // is otherwise unreachable without a mouse.
          tabIndex={0}
          role="region"
          aria-label="Onboarding step"
          className="flex-1 overflow-y-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        >
          <div className="mx-auto max-w-2xl px-6 py-8 lg:px-12 lg:py-10">
            {/* Mobile-only inline title (desktop uses the pinned header above) */}
            <div className="mb-6 lg:hidden">
              <h1 className="text-2xl font-semibold leading-tight text-white">
                {currentStepConfig?.label}
              </h1>
              {currentStepConfig?.description && (
                <p className="mt-1 text-sm text-muted">
                  {currentStepConfig.description}
                </p>
              )}
            </div>

            {renderStep()}
          </div>
        </div>

        {/* ─── Sticky footer ───────────────────────────────────────────
            This is the fix for the "buttons below the fold" bug. The
            footer lives outside the scroll container and is always
            visible at the bottom of the viewport. */}
        <footer className="shrink-0 border-t border-white/5 bg-background/80 backdrop-blur-xl">
          <div className="mx-auto max-w-2xl px-6 py-4 lg:px-12 lg:py-5">
            {submitError && (
              <div className="mb-3 rounded-lg border border-error/30 bg-error/10 p-3">
                <p className="text-sm text-red-300">{submitError}</p>
              </div>
            )}
            <StepNavigation
              onBack={isLoading ? NOOP : handleBack}
              onNext={isLoading ? NOOP : handleNext}
              canProceed={!isLoading && canProceed}
              isSubmitting={isBusy}
              isFirst={isFirstStep}
              isLast={isLastStep}
            />
            {/* Onboarding collects the owner's identity, business and bank
                details, so every step links the policy (SOC 2 R39). */}
            <PrivacyLinks className="mt-3 justify-center" linkClassName="text-xs text-muted hover:text-foreground" />
          </div>
        </footer>
      </main>
    </div>
  );
}
